import React, { useState, useEffect, useMemo } from 'react';
import { dashboardAPI, analyticsAPI, commissionAPI, clientsAPI, casesAPI } from '../lib/api';
import { scoreClient, scoreCase } from '../lib/dataQuality';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  TrendingUp, Users, Percent, Target, Building2, PoundSterling, BarChart3,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, AreaChart, Area,
} from 'recharts';
import { toast } from 'sonner';

const COLORS = ['#DC2626', '#2563EB', '#16A34A', '#D97706', '#7C3AED', '#EC4899', '#14B8A6'];

const Analytics = () => {
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [leadAnalytics, setLeadAnalytics] = useState(null);
  const [retention, setRetention] = useState(null);
  const [mortgageTypes, setMortgageTypes] = useState(null);
  const [commAnalytics, setCommAnalytics] = useState(null);
  const [lenderUsage, setLenderUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commFilters, setCommFilters] = useState({ start_date: '', end_date: '', product_filter: 'all', commission_status: 'all' });
  const [clientFeesFilters, setClientFeesFilters] = useState({ start_date: '', end_date: '', product_filter: 'all', fee_status: 'all' });
  const [clientFeesData, setClientFeesData] = useState(null);
  const [qualityData, setQualityData] = useState(null);
  const [qualityLoading, setQualityLoading] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsD, revenueD, leadD, retentionD, mtD, commD, lenderD, clientFeesD] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getRevenue(),
        dashboardAPI.getLeadAnalytics(),
        dashboardAPI.getRetention(),
        analyticsAPI.getMortgageTypes(),
        commissionAPI.getAnalytics({}),
        analyticsAPI.getLenderUsage(),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/commission/client-fees-analytics`, { credentials: 'include', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` } }).then(r => r.json()),
      ]);
      setStats(statsD);
      setRevenue(revenueD);
      setLeadAnalytics(leadD);
      setRetention(retentionD);
      setMortgageTypes(mtD);
      setCommAnalytics(commD);
      setLenderUsage(lenderD);
      setClientFeesData(clientFeesD);
    } catch (err) {
      console.error('Failed to load:', err);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const loadQualityData = async () => {
    if (qualityData) return; // already loaded
    try {
      setQualityLoading(true);
      const [clientsRes, casesRes] = await Promise.all([
        clientsAPI.getAll({ limit: 1000 }),
        casesAPI.getAll({ limit: 1000 }),
      ]);
      const allClients = clientsRes.clients || [];
      const allCases = casesRes.cases || [];

      const clientScores = allClients.map(c => ({ ...c, _quality: scoreClient(c) }));
      const caseScores = allCases.map(c => ({ ...c, _quality: scoreCase(c) }));

      const avgClientPct = clientScores.length ? Math.round(clientScores.reduce((s, c) => s + (c._quality?.pct || 0), 0) / clientScores.length) : 0;
      const avgCasePct = caseScores.length ? Math.round(caseScores.reduce((s, c) => s + (c._quality?.pct || 0), 0) / caseScores.length) : 0;

      const clientGreen = clientScores.filter(c => c._quality?.colour === 'green').length;
      const clientAmber = clientScores.filter(c => c._quality?.colour === 'amber').length;
      const clientRed = clientScores.filter(c => c._quality?.colour === 'red').length;
      const caseGreen = caseScores.filter(c => c._quality?.colour === 'green').length;
      const caseAmber = caseScores.filter(c => c._quality?.colour === 'amber').length;
      const caseRed = caseScores.filter(c => c._quality?.colour === 'red').length;

      // Most commonly missing client fields
      const clientMissingCount = {};
      clientScores.forEach(c => (c._quality?.missing || []).forEach(f => { clientMissingCount[f] = (clientMissingCount[f] || 0) + 1; }));
      const caseMissingCount = {};
      caseScores.forEach(c => (c._quality?.missing || []).forEach(f => { caseMissingCount[f] = (caseMissingCount[f] || 0) + 1; }));

      const topClientMissing = Object.entries(clientMissingCount).sort((a, b) => b[1] - a[1]).slice(0, 8);
      const topCaseMissing = Object.entries(caseMissingCount).sort((a, b) => b[1] - a[1]).slice(0, 8);

      // Worst records
      const worstClients = [...clientScores].sort((a, b) => (a._quality?.pct || 0) - (b._quality?.pct || 0)).slice(0, 10);
      const worstCases = [...caseScores].sort((a, b) => (a._quality?.pct || 0) - (b._quality?.pct || 0)).slice(0, 10);

      setQualityData({
        avgClientPct, avgCasePct,
        totalClients: allClients.length, totalCases: allCases.length,
        clientGreen, clientAmber, clientRed,
        caseGreen, caseAmber, caseRed,
        topClientMissing, topCaseMissing,
        worstClients, worstCases,
      });
    } catch (err) {
      toast.error('Failed to load quality data');
    } finally {
      setQualityLoading(false);
    }
  };

  const reloadCommAnalytics = async () => {
    try {
      const params = {};
      if (commFilters.start_date) params.start_date = commFilters.start_date;
      if (commFilters.end_date) params.end_date = commFilters.end_date;
      if (commFilters.product_filter !== 'all') params.product_filter = commFilters.product_filter;
      if (commFilters.commission_status !== 'all') params.commission_status = commFilters.commission_status;
      const data = await commissionAPI.getAnalytics(params);
      setCommAnalytics(data);
    } catch (err) {
      toast.error('Failed to reload commission analytics');
    }
  };

  const reloadClientFees = async () => {
    try {
      const params = new URLSearchParams();
      if (clientFeesFilters.start_date) params.set('start_date', clientFeesFilters.start_date);
      if (clientFeesFilters.end_date) params.set('end_date', clientFeesFilters.end_date);
      if (clientFeesFilters.product_filter !== 'all') params.set('product_filter', clientFeesFilters.product_filter);
      if (clientFeesFilters.fee_status !== 'all') params.set('fee_status', clientFeesFilters.fee_status);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/commission/client-fees-analytics?${params}`, { credentials: 'include', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const data = await res.json();
      setClientFeesData(data);
    } catch (err) {
      toast.error('Failed to reload client fees analytics');
    }
  };

  const fmt = (v) => {
    if (!v && v !== 0) return '£0';
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0 }).format(v);
  };
  const fmtSrc = (s) => s?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';

  const leadSourceData = useMemo(() => leadAnalytics?.by_lead_source?.map(i => ({
    name: fmtSrc(i._id), leads: i.total || 0, converted: i.completed || 0,
    revenue: i.total_commission || 0, conversionRate: i.conversion_rate || 0, avgLoan: i.avg_loan || 0,
  })) || [], [leadAnalytics]);
  const referralData = useMemo(() => leadAnalytics?.by_referral_partner?.map(i => ({ name: i._id || 'Unknown', count: i.count || 0, revenue: i.total_commission || 0 })) || [], [leadAnalytics]);
  const revenueByLender = useMemo(() => revenue?.by_lender?.map(i => ({ name: i._id || 'Unknown', value: i.total || 0, count: i.count || 0 })) || [], [revenue]);
  const revenueBySource = useMemo(() => revenue?.by_lead_source?.map(i => ({ name: fmtSrc(i._id), value: i.total || 0, count: i.count || 0 })) || [], [revenue]);
  const retentionByMonth = useMemo(() => retention?.expiring_by_month?.map(i => ({ month: i._id || 'N/A', count: i.count || 0, value: i.value || 0 })) || [], [retention]);
  const pipelineData = useMemo(() => stats?.status_counts ? Object.entries(stats.status_counts).map(([k, v]) => ({ name: k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), value: v })) : [], [stats]);

  // Mortgage type data
  const mtPieData = useMemo(() => mortgageTypes?.types?.map(t => ({ name: fmtSrc(t.mortgage_type), value: t.case_count })) || [], [mortgageTypes]);
  const mtBarData = useMemo(() => mortgageTypes?.types?.map(t => ({ name: fmtSrc(t.mortgage_type), commission: t.total_commission, avgLoan: t.avg_loan })) || [], [mortgageTypes]);

  // Commission analytics data
  const commByMonth = useMemo(() => commAnalytics?.by_month?.map(i => ({ name: i._id || 'N/A', commission: i.total || 0, proc_fees: i.proc_fees || 0 })) || [], [commAnalytics]);
  const commByLender = useMemo(() => commAnalytics?.by_lender?.map(i => ({ name: i._id || 'Unknown', value: i.total || 0 })) || [], [commAnalytics]);
  const commByProduct = useMemo(() => commAnalytics?.by_product?.map(i => ({ name: fmtSrc(i._id), value: i.total || 0 })) || [], [commAnalytics]);
  const commByLeadSrc = useMemo(() => commAnalytics?.by_lead_source?.map(i => ({ name: fmtSrc(i._id), value: i.total || 0 })) || [], [commAnalytics]);
  const commByAdvisor = useMemo(() => commAnalytics?.by_advisor?.map(i => ({ name: i.name || 'Unknown', value: i.total || 0, proc_fees: i.proc_fees || 0, cases: i.count || 0 })) || [], [commAnalytics]);

  if (loading) {
    return (<div className="flex items-center justify-center h-96"><div className="animate-spin h-8 w-8 border-4 border-red-600 border-t-transparent rounded-full" /></div>);
  }

  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="analytics-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>Analytics & Reports</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">Comprehensive business intelligence and insights</p>
      </div>

      {/* KPI Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { icon: Users, color: 'blue', value: stats?.total_clients || 0, label: 'Total Clients' },
          { icon: Percent, color: 'green', value: `${stats?.conversion_rate || 0}%`, label: 'Conversion Rate' },
          { icon: PoundSterling, color: 'purple', value: fmt(stats?.avg_loan_size), label: 'Avg Loan Size' },
          { icon: Target, color: 'red', value: stats?.completed_cases || 0, label: 'Completed Cases' },
          { icon: Building2, color: 'orange', value: fmt(retention?.retention_pipeline_value), label: 'Retention Value' },
        ].map(({ icon: Icon, color, value, label }) => (
          <Card key={label} className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`h-10 w-10 rounded-full bg-${color}-100 flex items-center justify-center`}><Icon className={`h-5 w-5 text-${color}-600`} /></div>
                <div><p className="text-2xl font-bold text-slate-900 dark:text-white">{value}</p><p className="text-sm text-slate-500 dark:text-slate-400">{label}</p></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="leads" className="w-full">
        <TabsList className="flex flex-wrap h-auto gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
          <TabsTrigger value="leads">Lead Analytics</TabsTrigger>
          <TabsTrigger value="mortgage-types" data-testid="mortgage-types-tab">Mortgage Types</TabsTrigger>
          <TabsTrigger value="commission-analytics" data-testid="commission-analytics-tab">Commission Analytics</TabsTrigger>
          <TabsTrigger value="client-fees">Client Fees</TabsTrigger>
          <TabsTrigger value="lender-analytics" data-testid="lender-analytics-tab">Lender Analytics</TabsTrigger>
          <TabsTrigger value="revenue">Revenue</TabsTrigger>
          <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
          <TabsTrigger value="retention">Retention</TabsTrigger>
          <TabsTrigger value="data-quality">Data Quality</TabsTrigger>
        </TabsList>

        {/* Lead Analytics Tab */}
        <TabsContent value="leads" className="mt-6 space-y-6">
          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
            <CardHeader><CardTitle>Lead Source Performance</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Lead Source</TableHead><TableHead className="text-right">Leads</TableHead><TableHead className="text-right">Converted</TableHead><TableHead className="text-right">Conversion Rate</TableHead><TableHead className="text-right">Revenue</TableHead><TableHead className="text-right">Avg Loan</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leadSourceData.map((i, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{i.name}</TableCell>
                        <TableCell className="text-right">{i.leads}</TableCell>
                        <TableCell className="text-right">{i.converted}</TableCell>
                        <TableCell className="text-right"><Badge className={i.conversionRate >= 50 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>{i.conversionRate}%</Badge></TableCell>
                        <TableCell className="text-right font-medium">{fmt(i.revenue)}</TableCell>
                        <TableCell className="text-right">{fmt(i.avgLoan)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
              <CardHeader><CardTitle>Conversion by Lead Source</CardTitle></CardHeader>
              <CardContent>
                <div className="h-[300px]"><ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <BarChart data={leadSourceData}><CartesianGrid strokeDasharray="3 3" stroke="#334155" /><XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#94a3b8" /><YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" /><Tooltip /><Legend /><Bar dataKey="leads" fill="#2563EB" radius={[4, 4, 0, 0]} name="Total Leads" /><Bar dataKey="converted" fill="#16A34A" radius={[4, 4, 0, 0]} name="Converted" /></BarChart>
                </ResponsiveContainer></div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
              <CardHeader><CardTitle>Top Referral Partners</CardTitle></CardHeader>
              <CardContent>
                {referralData.length === 0 ? <div className="text-center py-8 text-slate-500"><Users className="h-12 w-12 mx-auto text-slate-300 mb-4" /><p>No referral data available</p></div> : (
                  <div className="h-[300px]"><ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                    <BarChart data={referralData} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#334155" /><XAxis type="number" tick={{ fontSize: 12 }} stroke="#94a3b8" tickFormatter={(v) => `£${v/1000}k`} /><YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} stroke="#94a3b8" width={100} /><Tooltip formatter={(v) => fmt(v)} /><Bar dataKey="revenue" fill="#DC2626" radius={[0, 4, 4, 0]} name="Revenue" /></BarChart>
                  </ResponsiveContainer></div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Mortgage Types Tab */}
        <TabsContent value="mortgage-types" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {mortgageTypes?.types?.map((t) => (
              <Card key={t.mortgage_type} className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                <CardContent className="p-5">
                  <p className="text-sm text-slate-500 mb-1">{fmtSrc(t.mortgage_type)}</p>
                  <p className="text-3xl font-bold text-slate-900 dark:text-white dark:text-white">{t.case_count}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{t.percentage}% of total</p>
                  <div className="mt-2 pt-2 border-t border-slate-100">
                    <p className="text-xs text-slate-500">Commission: <span className="font-medium text-slate-700">{fmt(t.total_commission)}</span></p>
                    <p className="text-xs text-slate-500">Avg Loan: <span className="font-medium text-slate-700">{fmt(t.avg_loan)}</span></p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
              <CardHeader><CardTitle>Mortgage Type Distribution</CardTitle></CardHeader>
              <CardContent>
                <div className="h-[300px]"><ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <PieChart><Pie data={mtPieData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>{mtPieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip /><Legend /></PieChart>
                </ResponsiveContainer></div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
              <CardHeader><CardTitle>Commission by Mortgage Type</CardTitle></CardHeader>
              <CardContent>
                <div className="h-[300px]"><ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <BarChart data={mtBarData}><CartesianGrid strokeDasharray="3 3" stroke="#334155" /><XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#94a3b8" /><YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `£${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`} /><Tooltip formatter={(v) => fmt(v)} /><Legend /><Bar dataKey="commission" fill="#DC2626" radius={[4, 4, 0, 0]} name="Commission" /><Bar dataKey="avgLoan" fill="#2563EB" radius={[4, 4, 0, 0]} name="Avg Loan Size" /></BarChart>
                </ResponsiveContainer></div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Commission Analytics Tab */}
        <TabsContent value="commission-analytics" className="mt-6 space-y-6">
          {/* Filters */}
          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-3 items-end">
                <div className="space-y-1"><Label className="text-xs">Start Date</Label><Input type="date" value={commFilters.start_date} onChange={(e) => setCommFilters({ ...commFilters, start_date: e.target.value })} className="w-40" /></div>
                <div className="space-y-1"><Label className="text-xs">End Date</Label><Input type="date" value={commFilters.end_date} onChange={(e) => setCommFilters({ ...commFilters, end_date: e.target.value })} className="w-40" /></div>
                <div className="space-y-1">
                  <Label className="text-xs">Product</Label>
                  <Select value={commFilters.product_filter} onValueChange={(v) => setCommFilters({ ...commFilters, product_filter: v })}>
                    <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="mortgage">Mortgage</SelectItem><SelectItem value="insurance">Insurance</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Commission Status</Label>
                  <Select value={commFilters.commission_status} onValueChange={(v) => setCommFilters({ ...commFilters, commission_status: v })}>
                    <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="submitted_to_lender">Submitted</SelectItem><SelectItem value="paid">Paid</SelectItem><SelectItem value="clawed_back">Clawed Back</SelectItem></SelectContent>
                  </Select>
                </div>
                <Button onClick={reloadCommAnalytics} size="sm" className="bg-red-600 hover:bg-red-700" data-testid="apply-comm-filters">Apply Filters</Button>
              </div>
            </CardContent>
          </Card>

          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { label: 'Total Commission', value: fmt(commAnalytics?.summary?.total_commission) },
              { label: 'Total Paid', value: fmt(commAnalytics?.summary?.total_paid) },
              { label: 'Total Pending', value: fmt(commAnalytics?.summary?.total_pending) },
              { label: 'Total Clawbacks', value: fmt(commAnalytics?.summary?.total_clawbacks) },
              { label: 'Total Client Fees', value: fmt(commAnalytics?.summary?.total_client_fees) },
              { label: 'Avg per Case', value: fmt(commAnalytics?.summary?.avg_commission) },
            ].map(({ label, value }) => (
              <Card key={label} className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-slate-500">{label}</p>
                  <p className="text-xl font-bold text-slate-900 dark:text-white mt-1">{value}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
              <CardHeader><CardTitle>Commission by Month</CardTitle></CardHeader>
              <CardContent>
                <div className="h-[300px]"><ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <BarChart data={commByMonth}><CartesianGrid strokeDasharray="3 3" stroke="#334155" /><XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#94a3b8" /><YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `£${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`} /><Tooltip formatter={(v) => fmt(v)} /><Legend /><Bar dataKey="commission" fill="#DC2626" radius={[4, 4, 0, 0]} name="Commission" /><Bar dataKey="proc_fees" fill="#2563EB" radius={[4, 4, 0, 0]} name="Proc Fees" /></BarChart>
                </ResponsiveContainer></div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
              <CardHeader><CardTitle>Commission by Lender</CardTitle></CardHeader>
              <CardContent>
                <div className="h-[300px]"><ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <BarChart data={commByLender} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#334155" /><XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `£${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`} /><YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} stroke="#94a3b8" width={100} /><Tooltip formatter={(v) => fmt(v)} /><Bar dataKey="value" fill="#DC2626" radius={[0, 4, 4, 0]} name="Commission" /></BarChart>
                </ResponsiveContainer></div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
              <CardHeader><CardTitle>Commission by Product Type</CardTitle></CardHeader>
              <CardContent>
                <div className="h-[300px]"><ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <PieChart><Pie data={commByProduct} cx="50%" cy="50%" outerRadius={100} paddingAngle={2} dataKey="value" label={({ name, value }) => `${name}: ${fmt(value)}`}>{commByProduct.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip formatter={(v) => fmt(v)} /></PieChart>
                </ResponsiveContainer></div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
              <CardHeader><CardTitle>Commission by Lead Source</CardTitle></CardHeader>
              <CardContent>
                <div className="h-[300px]"><ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <BarChart data={commByLeadSrc} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#334155" /><XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `£${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`} /><YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} stroke="#94a3b8" width={80} /><Tooltip formatter={(v) => fmt(v)} /><Bar dataKey="value" fill="#2563EB" radius={[0, 4, 4, 0]} name="Commission" /></BarChart>
                </ResponsiveContainer></div>
              </CardContent>
            </Card>
          </div>

          {/* Commission by Advisor Table */}
          {commByAdvisor.length > 0 && (
            <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
              <CardHeader><CardTitle>Commission by Advisor</CardTitle></CardHeader>
              <CardContent>
                <Table>
                  <TableHeader><TableRow><TableHead>Advisor</TableHead><TableHead className="text-right">Cases</TableHead><TableHead className="text-right">Commission</TableHead><TableHead className="text-right">Proc Fees</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {commByAdvisor.map((a, i) => (
                      <TableRow key={i}><TableCell className="font-medium">{a.name}</TableCell><TableCell className="text-right">{a.cases}</TableCell><TableCell className="text-right">{fmt(a.value)}</TableCell><TableCell className="text-right">{fmt(a.proc_fees)}</TableCell><TableCell className="text-right font-bold">{fmt(a.value + a.proc_fees)}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Revenue Tab */}
        <TabsContent value="revenue" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
              <CardHeader><CardTitle>Revenue by Lender</CardTitle></CardHeader>
              <CardContent>
                <div className="h-[300px]"><ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <PieChart><Pie data={revenueByLender} cx="50%" cy="50%" outerRadius={100} paddingAngle={2} dataKey="value" label={({ name, value }) => `${name}: ${fmt(value)}`}>{revenueByLender.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip formatter={(v) => fmt(v)} /></PieChart>
                </ResponsiveContainer></div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
              <CardHeader><CardTitle>Revenue by Lead Source</CardTitle></CardHeader>
              <CardContent>
                <div className="h-[300px]"><ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <BarChart data={revenueBySource} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#334155" /><XAxis type="number" tick={{ fontSize: 12 }} stroke="#94a3b8" tickFormatter={(v) => `£${v/1000}k`} /><YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} stroke="#94a3b8" width={80} /><Tooltip formatter={(v) => fmt(v)} /><Bar dataKey="value" fill="#2563EB" radius={[0, 4, 4, 0]} name="Revenue" /></BarChart>
                </ResponsiveContainer></div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Pipeline Tab */}
        <TabsContent value="pipeline" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
              <CardHeader><CardTitle>Pipeline Stage Distribution</CardTitle></CardHeader>
              <CardContent>
                <div className="h-[300px]"><ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <PieChart><Pie data={pipelineData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>{pipelineData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip /></PieChart>
                </ResponsiveContainer></div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
              <CardHeader><CardTitle>Completion vs Lost Ratio</CardTitle></CardHeader>
              <CardContent>
                <div className="h-[300px] flex items-center justify-center">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-8">
                      <div><p className="text-5xl font-bold text-green-600">{stats?.completed_cases || 0}</p><p className="text-slate-500 mt-2">Completed</p></div>
                      <div className="text-4xl text-slate-300">vs</div>
                      <div><p className="text-5xl font-bold text-red-600">{stats?.lost_cases || 0}</p><p className="text-slate-500 mt-2">Lost</p></div>
                    </div>
                    <div className="mt-8"><p className="text-3xl font-bold text-slate-900 dark:text-white dark:text-white">{stats?.conversion_rate || 0}%</p><p className="text-slate-500">Overall Conversion Rate</p></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Retention Tab */}
        <TabsContent value="retention" className="mt-6 space-y-6">
          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
            <CardHeader><CardTitle>Products Expiring by Month</CardTitle></CardHeader>
            <CardContent>
              <div className="h-[300px]"><ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                <AreaChart data={retentionByMonth}><CartesianGrid strokeDasharray="3 3" stroke="#334155" /><XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#94a3b8" /><YAxis yAxisId="left" tick={{ fontSize: 12 }} stroke="#94a3b8" /><YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} stroke="#94a3b8" tickFormatter={(v) => `£${v/1000}k`} /><Tooltip formatter={(v, name) => name === 'value' ? fmt(v) : v} /><Legend /><Area yAxisId="left" type="monotone" dataKey="count" stroke="#2563EB" fill="#2563EB" fillOpacity={0.3} name="Cases Expiring" /><Area yAxisId="right" type="monotone" dataKey="value" stroke="#DC2626" fill="#DC2626" fillOpacity={0.3} name="Loan Value" /></AreaChart>
              </ResponsiveContainer></div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
            <CardHeader><CardTitle>Products Expiring This Month</CardTitle></CardHeader>
            <CardContent>
              {retention?.expiring_this_month?.length === 0 ? (
                <div className="text-center py-8 text-slate-500"><BarChart3 className="h-12 w-12 mx-auto text-slate-300 mb-4" /><p>No products expiring this month</p></div>
              ) : (
                <div className="space-y-3">
                  {retention?.expiring_this_month?.map((i) => (
                    <div key={i.case_id} className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
                      <div><p className="font-medium">{i.client_name}</p><p className="text-sm text-slate-500 dark:text-slate-400">{i.lender_name} · Expires: {i.product_expiry_date}</p></div>
                      <p className="font-bold text-slate-900 dark:text-white">{fmt(i.loan_amount)}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Lender Analytics Tab */}
        <TabsContent value="lender-analytics" className="space-y-6 mt-6">
          {lenderUsage && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[
                { title: 'Most Used Lenders (All Time)', data: lenderUsage.all_time, color: 'red' },
                { title: 'Most Used Lenders (Last 12 Months)', data: lenderUsage.last_12_months, color: 'blue' },
                { title: 'Most Used Lenders - Buy To Let', data: lenderUsage.buy_to_let, color: 'amber' },
                { title: 'Most Used Lenders - Residential', data: lenderUsage.residential, color: 'green' },
              ].map((section) => (
                <Card key={section.title} className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                  <CardHeader><CardTitle className="text-base">{section.title}</CardTitle></CardHeader>
                  <CardContent>
                    {section.data && section.data.length > 0 ? (
                      <div className="space-y-2">
                        {section.data.map((item, idx) => (
                          <div key={item.lender} className="flex items-center gap-3">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${idx === 0 ? `bg-${section.color}-100 text-${section.color}-700` : 'bg-slate-100 text-slate-600'}`}>
                              {idx + 1}
                            </span>
                            <div className="flex-1 flex items-center justify-between">
                              <span className="text-sm font-medium">{item.lender}</span>
                              <div className="flex items-center gap-2">
                                <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                                  <div
                                    className="h-full rounded-full bg-red-500"
                                    style={{ width: `${Math.min((item.cases / (section.data[0]?.cases || 1)) * 100, 100)}%` }}
                                  />
                                </div>
                                <span className="text-sm font-bold text-slate-700 w-8 text-right">{item.cases}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 text-center py-6">No data available</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Client Fees Tab */}
        <TabsContent value="client-fees" className="mt-6 space-y-6">
          {/* Filters */}
          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-3 items-end">
                <div className="space-y-1"><Label className="text-xs">Start Date</Label><Input type="date" value={clientFeesFilters.start_date} onChange={(e) => setClientFeesFilters({ ...clientFeesFilters, start_date: e.target.value })} className="w-40" /></div>
                <div className="space-y-1"><Label className="text-xs">End Date</Label><Input type="date" value={clientFeesFilters.end_date} onChange={(e) => setClientFeesFilters({ ...clientFeesFilters, end_date: e.target.value })} className="w-40" /></div>
                <div className="space-y-1">
                  <Label className="text-xs">Product</Label>
                  <Select value={clientFeesFilters.product_filter} onValueChange={(v) => setClientFeesFilters({ ...clientFeesFilters, product_filter: v })}>
                    <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="mortgage">Mortgage</SelectItem><SelectItem value="insurance">Insurance</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Fee Status</Label>
                  <Select value={clientFeesFilters.fee_status} onValueChange={(v) => setClientFeesFilters({ ...clientFeesFilters, fee_status: v })}>
                    <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="paid">Paid</SelectItem></SelectContent>
                  </Select>
                </div>
                <Button onClick={reloadClientFees} size="sm" className="bg-red-600 hover:bg-red-700">Apply Filters</Button>
              </div>
            </CardContent>
          </Card>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Total Client Fees', value: fmt(clientFeesData?.summary?.total_fees) },
              { label: 'Total Paid', value: fmt(clientFeesData?.summary?.total_paid) },
              { label: 'Total Pending', value: fmt(clientFeesData?.summary?.total_pending) },
              { label: 'Avg Fee per Case', value: fmt(clientFeesData?.summary?.avg_fee) },
            ].map(({ label, value }) => (
              <Card key={label} className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-slate-500">{label}</p>
                  <p className="text-xl font-bold text-slate-900 dark:text-white mt-1">{value}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
              <CardHeader><CardTitle>Client Fees by Month</CardTitle></CardHeader>
              <CardContent>
                <div className="h-[300px]"><ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <BarChart data={clientFeesData?.by_month || []}><CartesianGrid strokeDasharray="3 3" stroke="#334155" /><XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#94a3b8" /><YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `£${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`} /><Tooltip formatter={(v) => fmt(v)} /><Bar dataKey="value" fill="#7C3AED" radius={[4, 4, 0, 0]} name="Client Fees" /></BarChart>
                </ResponsiveContainer></div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
              <CardHeader><CardTitle>Client Fees by Product Type</CardTitle></CardHeader>
              <CardContent>
                <div className="h-[300px]"><ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <PieChart><Pie data={clientFeesData?.by_product || []} cx="50%" cy="50%" outerRadius={100} paddingAngle={2} dataKey="value" label={({ name, value }) => `${fmtSrc(name)}: ${fmt(value)}`}>{(clientFeesData?.by_product || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip formatter={(v) => fmt(v)} /></PieChart>
                </ResponsiveContainer></div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800 lg:col-span-2">
              <CardHeader><CardTitle>Client Fees by Lender / Provider</CardTitle></CardHeader>
              <CardContent>
                <div className="h-[280px]"><ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <BarChart data={clientFeesData?.by_lender || []} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#334155" /><XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `£${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`} /><YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} stroke="#94a3b8" width={120} /><Tooltip formatter={(v) => fmt(v)} /><Bar dataKey="value" fill="#7C3AED" radius={[0, 4, 4, 0]} name="Client Fees" /></BarChart>
                </ResponsiveContainer></div>
              </CardContent>
            </Card>
          </div>

          {/* Cases Table */}
          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
            <CardHeader><CardTitle>Client Fee Details</CardTitle></CardHeader>
            <CardContent>
              {!clientFeesData?.cases?.length ? (
                <p className="text-center text-slate-500 py-8">No client fee records found</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Lender / Provider</TableHead>
                      <TableHead className="text-right">Client Fee</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date Paid</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clientFeesData.cases.map((c) => (
                      <TableRow key={c.case_id}>
                        <TableCell className="font-medium">{c.client_name}</TableCell>
                        <TableCell>{fmtSrc(c.product_type)}</TableCell>
                        <TableCell>{c.lender_name || c.insurance_provider || '-'}</TableCell>
                        <TableCell className="text-right font-bold">{fmt(c.client_fee)}</TableCell>
                        <TableCell>
                          {c.client_fee_status === 'paid'
                            ? <Badge className="bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">Paid</Badge>
                            : <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300">Pending</Badge>}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">{c.client_fee_paid_date || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Data Quality Tab */}
        <TabsContent value="data-quality" className="mt-6 space-y-6">
          {/* Trigger load on tab click */}
          {!qualityData && !qualityLoading && (
            <div className="text-center py-12">
              <button onClick={loadQualityData} className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors">
                Analyse Data Quality
              </button>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-3">Scans all clients and cases to calculate completeness scores</p>
            </div>
          )}
          {qualityLoading && (
            <div className="text-center py-12 text-slate-500 dark:text-slate-400">Analysing your data...</div>
          )}
          {qualityData && (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border-slate-200 dark:border-slate-700">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Avg Client Quality</p>
                    <p className={`text-3xl font-bold ${qualityData.avgClientPct >= 90 ? 'text-green-600' : qualityData.avgClientPct >= 70 ? 'text-yellow-600' : 'text-red-600'}`}>{qualityData.avgClientPct}%</p>
                    <p className="text-xs text-slate-500 mt-1">{qualityData.totalClients} clients scored</p>
                  </CardContent>
                </Card>
                <Card className="border-slate-200 dark:border-slate-700">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Avg Case Quality</p>
                    <p className={`text-3xl font-bold ${qualityData.avgCasePct >= 90 ? 'text-green-600' : qualityData.avgCasePct >= 70 ? 'text-yellow-600' : 'text-red-600'}`}>{qualityData.avgCasePct}%</p>
                    <p className="text-xs text-slate-500 mt-1">{qualityData.totalCases} cases scored</p>
                  </CardContent>
                </Card>
                <Card className="border-slate-200 dark:border-slate-700">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Client breakdown</p>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 inline-block" />{qualityData.clientGreen} complete</span>
                      <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400 inline-block" />{qualityData.clientAmber} partial</span>
                      <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" />{qualityData.clientRed} poor</span>
                    </div>
                    <div className="mt-2 flex rounded-full overflow-hidden h-2">
                      <div className="bg-green-500" style={{ width: `${qualityData.totalClients ? (qualityData.clientGreen/qualityData.totalClients)*100 : 0}%` }} />
                      <div className="bg-yellow-400" style={{ width: `${qualityData.totalClients ? (qualityData.clientAmber/qualityData.totalClients)*100 : 0}%` }} />
                      <div className="bg-red-500" style={{ width: `${qualityData.totalClients ? (qualityData.clientRed/qualityData.totalClients)*100 : 0}%` }} />
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-slate-200 dark:border-slate-700">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Case breakdown</p>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 inline-block" />{qualityData.caseGreen} complete</span>
                      <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400 inline-block" />{qualityData.caseAmber} partial</span>
                      <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" />{qualityData.caseRed} poor</span>
                    </div>
                    <div className="mt-2 flex rounded-full overflow-hidden h-2">
                      <div className="bg-green-500" style={{ width: `${qualityData.totalCases ? (qualityData.caseGreen/qualityData.totalCases)*100 : 0}%` }} />
                      <div className="bg-yellow-400" style={{ width: `${qualityData.totalCases ? (qualityData.caseAmber/qualityData.totalCases)*100 : 0}%` }} />
                      <div className="bg-red-500" style={{ width: `${qualityData.totalCases ? (qualityData.caseRed/qualityData.totalCases)*100 : 0}%` }} />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Most commonly missing fields */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="border-slate-200 dark:border-slate-700">
                  <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4 text-red-600" />Most Missing Client Fields</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Field</TableHead>
                          <TableHead className="text-right">Missing from</TableHead>
                          <TableHead className="text-right">% of clients</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {qualityData.topClientMissing.map(([field, count]) => (
                          <TableRow key={field}>
                            <TableCell className="font-medium">{field}</TableCell>
                            <TableCell className="text-right">{count} clients</TableCell>
                            <TableCell className="text-right">
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${count/qualityData.totalClients > 0.5 ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : count/qualityData.totalClients > 0.2 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300' : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'}`}>
                                {Math.round((count/qualityData.totalClients)*100)}%
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                        {qualityData.topClientMissing.length === 0 && (
                          <TableRow><TableCell colSpan={3} className="text-center text-slate-500 py-4">All client fields complete ✓</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                <Card className="border-slate-200 dark:border-slate-700">
                  <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4 text-red-600" />Most Missing Case Fields</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Field</TableHead>
                          <TableHead className="text-right">Missing from</TableHead>
                          <TableHead className="text-right">% of cases</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {qualityData.topCaseMissing.map(([field, count]) => (
                          <TableRow key={field}>
                            <TableCell className="font-medium">{field}</TableCell>
                            <TableCell className="text-right">{count} cases</TableCell>
                            <TableCell className="text-right">
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${count/qualityData.totalCases > 0.5 ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : count/qualityData.totalCases > 0.2 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300' : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'}`}>
                                {Math.round((count/qualityData.totalCases)*100)}%
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                        {qualityData.topCaseMissing.length === 0 && (
                          <TableRow><TableCell colSpan={3} className="text-center text-slate-500 py-4">All case fields complete ✓</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>

              {/* Worst records */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="border-slate-200 dark:border-slate-700">
                  <CardHeader><CardTitle className="text-base">Clients Needing Attention</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Client</TableHead>
                          <TableHead>Score</TableHead>
                          <TableHead>Missing</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {qualityData.worstClients.filter(c => (c._quality?.pct || 100) < 100).map(c => (
                          <TableRow key={c.client_id}>
                            <TableCell className="font-medium">{c.first_name} {c.last_name}</TableCell>
                            <TableCell>
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${c._quality?.badgeCls}`}>{c._quality?.pct}%</span>
                            </TableCell>
                            <TableCell className="text-xs text-slate-500 dark:text-slate-400">{(c._quality?.missing || []).slice(0, 3).join(', ')}{c._quality?.missing?.length > 3 ? ` +${c._quality.missing.length - 3} more` : ''}</TableCell>
                          </TableRow>
                        ))}
                        {qualityData.worstClients.every(c => (c._quality?.pct || 100) >= 100) && (
                          <TableRow><TableCell colSpan={3} className="text-center text-slate-500 py-4">All clients complete ✓</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                <Card className="border-slate-200 dark:border-slate-700">
                  <CardHeader><CardTitle className="text-base">Cases Needing Attention</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Case</TableHead>
                          <TableHead>Client</TableHead>
                          <TableHead>Score</TableHead>
                          <TableHead>Missing</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {qualityData.worstCases.filter(c => (c._quality?.pct || 100) < 100).map(c => (
                          <TableRow key={c.case_id}>
                            <TableCell className="font-mono text-xs">{c.case_id}</TableCell>
                            <TableCell className="text-sm">{c.client_name || '—'}</TableCell>
                            <TableCell>
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${c._quality?.badgeCls}`}>{c._quality?.pct}%</span>
                            </TableCell>
                            <TableCell className="text-xs text-slate-500 dark:text-slate-400">{(c._quality?.missing || []).slice(0, 2).join(', ')}{c._quality?.missing?.length > 2 ? ` +${c._quality.missing.length - 2} more` : ''}</TableCell>
                          </TableRow>
                        ))}
                        {qualityData.worstCases.every(c => (c._quality?.pct || 100) >= 100) && (
                          <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-4">All cases complete ✓</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>

              <div className="flex justify-end">
                <button onClick={() => { setQualityData(null); loadQualityData(); }} className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 underline">
                  Refresh scores
                </button>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Analytics;
